import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { NodeSelectionTemplateSummary } from '@cast/types';
import { EdgeLocation } from '@cast/types-omni';

import { apiClient } from '@cast/infrastructure-api';
import { EnhancedEdgeLocation } from 'features/cluster/autoscaler/edge-locations/types';
import {
  getEdgeLocationMappedState,
  getEdgeLocationProvider,
} from 'features/cluster/autoscaler/edge-locations/utils';
import { isNotFoundError } from 'utils/api-utils';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useSelectionTemplatesQuery } from '../autoscaler';

const OMNI_EDGE_LOCATIONS_QUERY_KEY = 'omni-edge-locations';

export const getClusterEdgeLocationsQueryKey = ({
  clusterId,
  selectionTemplates,
}: {
  clusterId?: string;
  selectionTemplates?: NodeSelectionTemplateSummary[];
} = {}) => {
  if (clusterId && selectionTemplates) {
    return [
      OMNI_EDGE_LOCATIONS_QUERY_KEY,
      clusterId,
      selectionTemplates,
    ] as const;
  }
  if (clusterId) {
    return [OMNI_EDGE_LOCATIONS_QUERY_KEY, clusterId] as const;
  }
  return [OMNI_EDGE_LOCATIONS_QUERY_KEY] as const;
};

const enhanceEdgeLocation = (
  edgeLocation: EdgeLocation,
  nodeTemplates?: NodeSelectionTemplateSummary[]
): EnhancedEdgeLocation => {
  const templateUsage = (nodeTemplates || []).reduce(
    (acc, { template }) => {
      if (
        template?.edgeLocationConfigs?.some(
          (edgeLocationConfig) =>
            edgeLocationConfig.edgeLocationId === edgeLocation.id
        )
      ) {
        if (template.name) {
          acc.templateNames.push(template.name);
        }
        acc.totalUsed++;

        if (template?.isEnabled) {
          acc.totalEnabled++;
        }
      }

      return acc;
    },
    { totalUsed: 0, totalEnabled: 0, templateNames: [] as string[] }
  );

  return {
    ...edgeLocation,
    mappedState: getEdgeLocationMappedState(edgeLocation, templateUsage),
    templateUsage,
    provider: getEdgeLocationProvider(edgeLocation),
  };
};

const shouldEnableQuery = (
  includeNodeTemplates: boolean,
  selectionTemplates?: NodeSelectionTemplateSummary[]
) => {
  if (includeNodeTemplates) {
    return !!selectionTemplates;
  }

  return true;
};

const getClusterEdgeLocationsQueryOptions = ({
  clusterId,
  organizationId,
  selectionTemplates,
  includeNodeTemplates,
  enabled,
  refetchInterval,
}: {
  clusterId: string;
  organizationId: string;
  selectionTemplates?: NodeSelectionTemplateSummary[];
  includeNodeTemplates: boolean;
  enabled: boolean;
  refetchInterval: QueryRefetchInterval<EnhancedEdgeLocation[]>;
}) =>
  queryOptions({
    queryKey: getClusterEdgeLocationsQueryKey({
      clusterId,
      selectionTemplates,
    }),
    queryFn: async () => {
      const { data } = await apiClient.omni
        .listEdgeLocations({
          organizationId,
          clusterId,
        })
        .catch((error) => {
          // If cluster does not have OMNI onboarded, it will return 404
          if (isNotFoundError(error)) {
            return { data: { items: [] } };
          }
          throw error;
        });

      if (!data.items) {
        return [];
      }

      return data.items.map((edgeLocation) =>
        enhanceEdgeLocation(edgeLocation, selectionTemplates)
      );
    },
    enabled:
      enabled &&
      !!clusterId &&
      shouldEnableQuery(includeNodeTemplates, selectionTemplates),
    refetchInterval,
  });

export const useGetClusterEdgeLocationsQuery = ({
  clusterId,
  includeNodeTemplates = false,
  enabled = true,
  refetchInterval = false,
}: {
  clusterId: string;
  includeNodeTemplates?: boolean;
  enabled?: boolean;
  refetchInterval?: QueryRefetchInterval<EnhancedEdgeLocation[]>;
}) => {
  const { currentOrganization } = useOrganizations();
  const { selectionTemplates } = useSelectionTemplatesQuery({
    clusterId,
    enabled: enabled && includeNodeTemplates,
  });

  const {
    data: edgeLocations,
    isLoading,
    error,
    refetch,
  } = useQuery(
    getClusterEdgeLocationsQueryOptions({
      clusterId,
      organizationId: currentOrganization?.id ?? '',
      // Only key/enrich by selection templates when the caller asked for them.
      // useSelectionTemplatesQuery is disabled here otherwise, but a disabled
      // query still returns cached data — so leaking it into the query key
      // makes the key change (new array reference) whenever templates are
      // invalidated, forcing a fresh pending fetch instead of a background one.
      selectionTemplates: includeNodeTemplates ? selectionTemplates : undefined,
      includeNodeTemplates,
      enabled,
      refetchInterval,
    })
  );

  return {
    edgeLocations,
    isLoading,
    error,
    refetch,
  };
};
